import { Router } from '@angular/router';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ApplicationService } from 'app/services/application.service';
import { FooterComponent } from 'app/components/footer/footer.component';
import { HeaderComponent } from 'app/components/header/header.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { PartnershipDialogComponent } from '../partnership-dialog/partnership-dialog.component';
import {
    trigger,
    state,
    style,
    animate,
    transition,
} from '@angular/animations';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { CampaignsSectionComponent } from './components/campaigns-section/campaigns-section.component';
import { ToastrService } from 'ngx-toastr';
import { WhatsNewBannerComponent } from './components/whats-new-banner/whats-new-banner.component';
import { NewsSectionComponent } from './components/news-section/news-section.component';
import { InsightsSectionComponent } from './components/insights-section/insights-section.component';
import { formatUrlEncode, htmlToString } from 'app/shared/utils';

@Component({
    selector: 'app-partnerships-campaigns',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        FooterComponent,
        HeaderComponent,
        MatButtonModule,
        MatDialogModule,
        TranslateModule,
        MatSnackBarModule,
        SlickCarouselModule,
        CampaignsSectionComponent,
        WhatsNewBannerComponent,
        NewsSectionComponent,
        InsightsSectionComponent,
    ],
    templateUrl: './partnerships-campaigns.component.html',
    styleUrl: './partnerships-campaigns.component.scss',
    animations: [
        trigger('fadeInOut', [
            state(
                'void',
                style({
                    opacity: 0,
                }),
            ),
            transition('void => *', animate(1000)),
            transition('* => void', animate(0)),
        ]),
        trigger('fadeInOut2', [
            state(
                'void',
                style({
                    opacity: 0,
                }),
            ),
            transition('void => *', animate(1000)),
            transition('* => void', animate(800)),
        ]),
    ],
})
export class PartnershipsCampaignsComponent {
    EXTERNAL = 'External';
    INTERNAL = 'Internal';

    // Data arrays for different categories
    bannerData: any = null; // Single banner item
    campaignData: any = [];
    newsData: any = [];
    insightsData: any = [];
    partnershipData: any = [];
    whatsnewData: any = [];
    // Language-specific arrays
    whatsNewBM: any = [];
    whatsNewEN: any = [];
    bannerBM: any = [];
    bannerEN: any = [];
    arrBM: any = [];
    arrEN: any = [];
    arrNewsBM: any = [];
    arrNewsEN: any = [];
    arrInsightsBM: any = [];
    arrInsightsEN: any = [];
    arrPartnerBM: any = [];
    arrPartnerEN: any = [];
    currentSlide: number = 0;
    slides_mobile = [
        {
            img: '/assets/images/slider-home/campaign-mobile-touch-n-go-bm.webp',
            id: 3,
        },
    ];
    slideConfig = {
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: true,
        arrows: true,
        autoplay: true,
        autoplaySpeed: 5000,
        prevArrow:
            '<button class="slick-prev"><img src="/assets/images/icons/arrow-left.svg" alt="Previous" /></button>',
        nextArrow:
            '<button class="slick-next"><img src="/assets/images/icons/arrow-right.svg" alt="Next" /></button>',
    };

    selectedLanguage: any;

    isAuthenticated: boolean = false;
    showMoreCampaignsStatus: boolean = false;
    showMorePartnershipsStatus: boolean = false;

    navCount: number = 0;
    navCountP: number = 0;
    toogle: boolean = false;
    toggleP: boolean = false;

    whatsNewTitle: string = '';
    whatsNewDesc: string = '';

    constructor(
        private router: Router,
        public dialog: MatDialog,
        private toastr: ToastrService,
        private applicationService: ApplicationService,
    ) {
        this.isAuthenticated =
            localStorage.getItem('isAuthenticated') == 'true';
    }

    ngOnInit() {
        this.selectedLanguage = this.readLocalStorageValue('language');
        this.getBannerData();
        this.getCampaignsList();
        this.getNewsList();
        this.getInsightsList();
        this.getPartnershipList();
        this.getWhatsNewData();
    }

    getCampaignsList() {
        this.applicationService
            .getCampaign('WHATSNEW-CAMPAIGN', true)
            .subscribe({
                next: (data) => {
                    const today = new Date().setHours(0, 0, 0, 0);

                    let filteredData = data.filter((item) => {
                        if (item?.campaignStartDate && item?.campaignEndDate) {
                            const campaignStartDate = new Date(
                                item.campaignStartDate,
                            ).setHours(0, 0, 0, 0);
                            const campaignEndDate = new Date(
                                item.campaignEndDate,
                            ).setHours(0, 0, 0, 0);
                            return (
                                item.status === 'ACTIVE' &&
                                campaignStartDate <= today &&
                                campaignEndDate >= today
                            );
                        }
                        return false;
                    });

                    this.processLanguageData(filteredData, 'campaign');
                },
                error: (err) => {
                    console.log('Error: ', err);
                },
            });
    }

    processLanguageData(data: any[], type: string) {
        const bmArray = [];
        const enArray = [];

        data.forEach((el: any) => {
            if (el?.campaigndetails?.length) {
                el.campaigndetails.forEach((detail: any) => {
                    const newObj = {
                        ...el,
                        campaignUrl: detail.campaignUrl,
                        campaignDetUrl: detail.campaignDetUrl,
                        campaigndetails: detail,
                    };

                    if (detail.languages === 'BM') {
                        bmArray.push(newObj);
                    } else if (detail.languages === 'EN') {
                        enArray.push(newObj);
                    }
                });
            }
        });

        // Assign to appropriate arrays based on type
        switch (type) {
            case 'campaign':
                this.arrBM = bmArray;
                this.arrEN = enArray;
                this.campaignData =
                    this.selectedLanguage === 'bm' ? bmArray : enArray;
                break;
            case 'news':
                this.arrNewsBM = bmArray;
                this.arrNewsEN = enArray;
                this.newsData =
                    this.selectedLanguage === 'bm' ? bmArray : enArray;
                break;
            case 'insights':
                this.arrInsightsBM = bmArray;
                this.arrInsightsEN = enArray;
                this.insightsData =
                    this.selectedLanguage === 'bm' ? bmArray : enArray;
                break;
            case 'whatsnew':
                this.whatsNewBM = bmArray;
                this.whatsNewEN = enArray;
                this.whatsnewData =
                    this.selectedLanguage === 'bm' ? bmArray : enArray;
                break;
            case 'banner':
                this.bannerBM = bmArray;
                this.bannerEN = enArray;
                this.bannerData =
                    this.selectedLanguage === 'bm' ? bmArray : enArray;
                break;
        }
    }
    getNewsList() {
        this.applicationService.getCampaign('WHATSNEW-NEWS', true).subscribe({
            next: (data) => {
                const filteredData = data.filter(
                    (item) => item.status === 'ACTIVE',
                );
                this.processLanguageData(filteredData, 'news');
            },
            error: (err) => {
                console.log('Error: ', err);
            },
        });
    }

    getInsightsList() {
        this.applicationService
            .getCampaign('WHATSNEW-INSIGHTS', true)
            .subscribe({
                next: (data) => {
                    const filteredData = data.filter(
                        (item) => item.status === 'ACTIVE',
                    );
                    this.processLanguageData(filteredData, 'insights');
                },
                error: (err) => {
                    console.log('Error: ', err);
                },
            });
    }

    getWhatsNewData() {
        this.applicationService.getCampaign('WHATSNEW', true).subscribe({
            next: (data) => {
                const filteredData = data.filter(
                    (item) => item.status === 'ACTIVE',
                );
                this.processLanguageData(filteredData, 'whatsnew');
                this.processWhatsNew();
            },
            error: (err) => {
                console.log('Error: ', err);
            },
        });
    }

    processWhatsNew() {
        if (this.whatsnewData && this.whatsnewData.length > 0) {
            this.whatsNewTitle =
                this.whatsnewData &&
                this.whatsnewData[0].campaigndetails.campaignName;
            this.whatsNewDesc =
                this.whatsnewData &&
                this.whatsnewData[0].campaigndetails.campaignDescription;
        }
    }

    getBannerData() {
        this.applicationService.getCampaign('WHATSNEW-BANNER', true).subscribe({
            next: (data) => {
                const filteredData = data.filter(
                    (item) => item.status === 'ACTIVE',
                );
                this.processLanguageData(filteredData, 'banner');
            },
            error: (err) => {
                console.log('Error: ', err);
            },
        });
    }

    getPartnershipList() {
        this.applicationService.getPartnership().subscribe({
            next: (data) => {
                // Get today's date
                const today = new Date();
                let filteredData = data.filter((item) => {
                    const pStartDate = new Date(item.pStartDate);
                    const pEndDate = new Date(item.pEndDate);
                    return (
                        item.status === 'ACTIVE' &&
                        pStartDate <= today && // pStartDate is same or before today's date
                        pEndDate >= today // pEndDate is same or after today's date
                    );
                });

                const listingPartnershipData = filteredData;
                /* TO SEPARATE EN AND BM DATA SO THAT WHEN CHANGES HAPPENS IT WONT DISPLAY WRONG DATA */
                this.arrPartnerBM = [];
                this.arrPartnerEN = [];
                listingPartnershipData.forEach((el: any) => {
                    let newObj = {};
                    if (
                        el?.partnershipDetails &&
                        el?.partnershipDetails?.length
                    ) {
                        if (el?.partnershipDetails[0]?.languages == 'BM') {
                            newObj = { ...el };
                            newObj['partnershipDetails'] =
                                el?.partnershipDetails[0];
                            this.arrPartnerBM.push(newObj);
                        } else if (
                            el?.partnershipDetails[0]?.languages == 'EN'
                        ) {
                            newObj = { ...el };
                            newObj['partnershipDetails'] =
                                el?.partnershipDetails[0];
                            this.arrPartnerEN.push(newObj);
                        }

                        if (el?.partnershipDetails[1]?.languages == 'BM') {
                            newObj = { ...el };
                            newObj['partnershipDetails'] =
                                el?.partnershipDetails[1];
                            this.arrPartnerBM.push(newObj);
                        } else if (
                            el?.partnershipDetails[1]?.languages == 'EN'
                        ) {
                            newObj = { ...el };
                            newObj['partnershipDetails'] =
                                el?.partnershipDetails[1];
                            this.arrPartnerEN.push(newObj);
                        }
                    }
                });

                if (this.selectedLanguage == 'bm') {
                    this.partnershipData = this.arrPartnerBM;
                } else {
                    this.partnershipData = this.arrPartnerEN;
                }
            },
            error: (err) => {
                // this._snackBar.open(err.error.message, 'X', {
                //     duration: 3000,
                // });
            },
        });
    }

    openDialog(id: any) {
        this.applicationService.getPartnershipDetailsById(id).subscribe({
            next: (data) => {
                /* TO SEPARATE EN AND BM DATA SO THAT WHEN CHANGES HAPPENS IT WONT DISPLAY WRONG DATA */
                let bmDetail = {};
                let enDetail = {};
                let newObj = {};
                if (
                    data?.partnershipDetails &&
                    data?.partnershipDetails?.length
                ) {
                    if (data?.partnershipDetails[0]?.languages == 'BM') {
                        newObj = { ...data };
                        newObj['partnershipDetails'] =
                            data?.partnershipDetails[0];
                        bmDetail = newObj;
                    } else if (data?.partnershipDetails[0]?.languages == 'EN') {
                        newObj = { ...data };
                        newObj['partnershipDetails'] =
                            data?.partnershipDetails[0];
                        enDetail = newObj;
                    }

                    if (data?.partnershipDetails[1]?.languages == 'BM') {
                        newObj = { ...data };
                        newObj['partnershipDetails'] =
                            data?.partnershipDetails[1];
                        bmDetail = newObj;
                    } else if (data?.partnershipDetails[1]?.languages == 'EN') {
                        newObj = { ...data };
                        newObj['partnershipDetails'] =
                            data?.partnershipDetails[1];
                        enDetail = newObj;
                    }
                }

                if (this.selectedLanguage == 'bm') {
                    this.dialog.open(PartnershipDialogComponent, {
                        width: '869px',
                        disableClose: true,
                        data: { partnership: bmDetail },
                    });
                } else {
                    this.dialog.open(PartnershipDialogComponent, {
                        width: '869px',
                        disableClose: true,
                        data: { partnership: enDetail },
                    });
                }
            },
            error: (err) => {
                console.log('Error: ', err);
            },
        });
    }

    readLocalStorageValue(key: string) {
        let value = localStorage.getItem(key);
        return value;
    }

    switchedLanguage(data: any) {
        this.selectedLanguage = data;

        // Update campaign data
        this.campaignData =
            this.selectedLanguage === 'bm' ? this.arrBM : this.arrEN;

        // Update news data
        this.newsData =
            this.selectedLanguage === 'bm' ? this.arrNewsBM : this.arrNewsEN;

        // Update insights data
        this.insightsData =
            this.selectedLanguage === 'bm'
                ? this.arrInsightsBM
                : this.arrInsightsEN;

        // Update partnership data
        this.partnershipData =
            this.selectedLanguage === 'bm'
                ? this.arrPartnerBM
                : this.arrPartnerEN;

        // Update banner data
        this.bannerData =
            this.selectedLanguage === 'bm' ? this.bannerBM : this.bannerEN;

        // update whatsnew data
        this.whatsnewData =
            this.selectedLanguage === 'bm' ? this.whatsNewBM : this.whatsNewEN;

        // update whatsNewTitle and whatsNewDesc when whatsnew data changes
        this.processWhatsNew();
    }

    showMoreCampaigns() {
        this.showMoreCampaignsStatus = true;
    }

    onClickImageRoute = (routePath: string) => {
        window.location.href = routePath;
    };

    showMorePartnerships() {
        this.showMorePartnershipsStatus = true;
    }

    showLessCampaigns() {
        this.showMoreCampaignsStatus = false;
        document.getElementById('desktopCampaign').scrollIntoView();
    }

    showLessPartnerships() {
        this.showMorePartnershipsStatus = false;
        document.getElementById('desktopPartnership').scrollIntoView();
    }

    viewCampaignDetails(data: any) {
        let whitespace = new Set([' ', '\t', '\n']);
        if (data?.campaigndetails?.isCampaignRequired == 'Yes') {
            let language = '?language=' + this.selectedLanguage;
            let url: any =
                '/campaign/' +
                data.id +
                '/' +
                formatUrlEncode(
                    htmlToString(data['campaigndetails'].campaignDescription),
                );
            // + language;
            this.router.navigateByUrl(url);
        } else {
            if (data?.campaignUrl) {
                if (
                    (data?.campaignUrl ?? '').includes('https://') ||
                    (data?.campaignUrl ?? '').includes('http://') ||
                    (data?.campaignUrl ?? '').includes('www') ||
                    (data?.campaignUrl ?? '').includes('/')
                ) {
                    if (data?.ctaType == this.EXTERNAL) {
                        window.open(data?.campaignUrl, '_blank');
                    } else {
                        window.open(data?.campaignUrl, '_self');
                    }
                } else {
                    for (let i = 0; i < data?.campaignUrl.length; i++) {
                        if (whitespace.has(data?.campaignUrl[i])) {
                            if (this.selectedLanguage == 'bm') {
                                this.toastr.error(
                                    'Oops. Terdapat ralat dalam sistem.',
                                );
                            } else {
                                this.toastr.error('Oops.Something went wrong.');
                            }
                        } else {
                            let productCode = data?.campaignUrl;
                            if (this.isAuthenticated) {
                                this.router.navigate(['/business-financing'], {
                                    queryParams: {
                                        productCode: productCode,
                                    },
                                });
                            } else {
                                this.router.navigate(['/login'], {
                                    queryParams: {
                                        returnUrl: `/business-financing?productCode=${productCode}`,
                                    },
                                });
                            }
                        }
                    }
                }
            } else {
                if (this.selectedLanguage == 'bm') {
                    this.toastr.error('Oops. Terdapat ralat dalam sistem.');
                } else {
                    this.toastr.error('Oops.Something went wrong.');
                }
            }
        }
    }

    slideTabPrevious() {
        this.navCount--;
        this.toogle = !this.toogle;
    }

    slideTabNext() {
        this.navCount++;
        this.toogle = !this.toogle;
    }

    slideTabPreviousP() {
        this.navCountP--;
        this.toggleP = !this.toggleP;
    }

    slideTabNextP() {
        this.navCountP++;
        this.toggleP = !this.toggleP;
    }

    afterChange(e) {
        this.currentSlide = e.currentSlide;
    }

    beforeChange(e) {
        this.currentSlide = e.currentSlide;
    }
}
